<script setup>
import { nextTick, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

defineProps({
  accountId: {
    type: [String, Number],
    required: true,
  },
  isAdministrator: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const isOpen = ref(false);
const trigger = ref(null);
const menu = ref(null);

const menuItems = () =>
  Array.from(menu.value?.querySelectorAll('[role="menuitem"]') || []);

const focusMenuItem = async index => {
  await nextTick();
  const items = menuItems();
  const normalizedIndex = (index + items.length) % items.length;
  items[normalizedIndex]?.focus();
};

const openMenu = (focusIndex = 0) => {
  isOpen.value = true;
  focusMenuItem(focusIndex);
};

const closeMenu = () => {
  isOpen.value = false;
};

const closeMenuAndRestoreFocus = () => {
  closeMenu();
  nextTick(() => trigger.value?.$el?.focus());
};

const toggleMenu = () => {
  if (isOpen.value) {
    closeMenu();
    return;
  }

  openMenu();
};

const handleTriggerKeydown = event => {
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    openMenu();
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    openMenu(-1);
  }
};

const handleMenuKeydown = event => {
  if (event.key === 'Escape') {
    event.preventDefault();
    closeMenuAndRestoreFocus();
    return;
  }

  if (event.key === 'Tab') {
    closeMenu();
    return;
  }

  const items = menuItems();
  const currentIndex = items.indexOf(document.activeElement);
  const keyTarget = {
    ArrowDown: currentIndex + 1,
    ArrowUp: currentIndex - 1,
    Home: 0,
    End: items.length - 1,
  }[event.key];

  if (keyTarget === undefined) return;

  event.preventDefault();
  focusMenuItem(keyTarget);
};
</script>

<template>
  <div v-on-click-outside="closeMenu" class="relative flex-shrink-0">
    <Button
      ref="trigger"
      type="button"
      icon="i-lucide-settings"
      color="slate"
      variant="outline"
      size="sm"
      :aria-label="t('COMMAND_BAR.SECTIONS.SETTINGS')"
      :aria-expanded="isOpen"
      aria-controls="essential-configuration-menu"
      aria-haspopup="menu"
      :title="t('COMMAND_BAR.SECTIONS.SETTINGS')"
      @click="toggleMenu"
      @keydown="handleTriggerKeydown"
    />

    <ul
      v-if="isOpen"
      id="essential-configuration-menu"
      ref="menu"
      role="menu"
      :aria-label="t('COMMAND_BAR.SECTIONS.SETTINGS')"
      class="absolute right-0 z-50 mt-2 grid w-56 max-w-[calc(100vw-2rem)] list-none gap-1 rounded-xl border border-n-weak bg-n-alpha-3 p-2 shadow-lg backdrop-blur-[100px] reset-base"
      @keydown="handleMenuKeydown"
    >
      <li v-if="isAdministrator" role="none">
        <router-link
          role="menuitem"
          tabindex="-1"
          class="flex w-full items-center gap-3 rounded-lg p-2 text-sm text-n-slate-12 hover:bg-n-alpha-2 focus-visible:bg-n-alpha-2 focus-visible:outline-none"
          :to="frontendURL(`accounts/${accountId}/essential/admin`)"
          @click="closeMenu"
        >
          <i class="i-lucide-shield-check size-4 text-n-slate-11" />
          <span>{{ t('ESSENTIAL.HEADER.OPEN_ADMIN') }}</span>
        </router-link>
      </li>
      <li role="none">
        <router-link
          role="menuitem"
          tabindex="-1"
          class="flex w-full items-center gap-3 rounded-lg p-2 text-sm text-n-slate-12 hover:bg-n-alpha-2 focus-visible:bg-n-alpha-2 focus-visible:outline-none"
          :to="frontendURL(`accounts/${accountId}/dashboard`)"
          @click="closeMenu"
        >
          <i class="i-lucide-monitor size-4 text-n-slate-11" />
          <span>{{ t('ESSENTIAL.HEADER.OPEN_PRO') }}</span>
        </router-link>
      </li>
    </ul>
  </div>
</template>
